/**
 * PaintedSceneAudioManager — per-scene ambient audio beds with crossfade
 * and terrain-aware footstep SFX for painted journey scenes.
 *
 * Uses Web Audio API directly (no Phaser sound manager) for precise gain
 * scheduling and crossfade control. Respects the global AudioSettings
 * from SettingsPersistence.
 *
 * Ambient beds are procedurally generated from manifest ambient keys
 * (e.g. "ambient-hill-wind" → wind profile). Footsteps reuse the layered
 * terrain system from OverworldAudioManager, adapted for the painted
 * scene walker.
 */

import { loadSettings } from '../ui/SettingsPersistence';

// ── Ambient bed types ─────────────────────────────────────────────────────

type AmbientProfile =
  | 'wind'
  | 'fire'
  | 'meadow'
  | 'birch'
  | 'brook'
  | 'village'
  | 'oven'
  | 'chimes'
  | 'forge'
  | 'glow'
  | 'echo'
  | 'steam'
  | 'silence'
  | 'ice'
  | 'drip'
  | 'hearth'
  | 'crunch';

type FootstepLayer = 'leaves' | 'grass' | 'dirt' | 'stone';
export type PaintedTerrainType = 'grass' | 'dirt' | 'stone' | 'leaves' | 'water_edge';

interface FootstepLayerParams {
  method: 'osc' | 'noise';
  freq: number;
  oscType: OscillatorType;
  dur: number;
  filterType?: BiquadFilterType;
  filterQ?: number;
  pitchDecay: number;
}

type FootstepMixProfile = Record<FootstepLayer, number>;

interface AmbientSlot {
  source: AudioBufferSourceNode;
  gain: GainNode;
}

// ── Constants ─────────────────────────────────────────────────────────────

const AMBIENT_CROSSFADE_MS = 2000;
const FOOTSTEP_INTERVAL_MS = 350;
const AMBIENT_BUFFER_DURATION = 4;

const FOOTSTEP_LAYERS: Record<FootstepLayer, FootstepLayerParams> = {
  leaves: {
    method: 'noise', freq: 2200, oscType: 'sine',
    dur: 0.09, filterType: 'bandpass', filterQ: 0.8, pitchDecay: 1,
  },
  grass: {
    method: 'noise', freq: 400, oscType: 'sine',
    dur: 0.08, filterType: 'lowpass', filterQ: 0.5, pitchDecay: 1,
  },
  // Was a 90Hz triangle sweeping to 27Hz. A triangle's odd harmonics land at
  // 270/450/630Hz, and on any speaker without real bass those are all you hear
  // — so a footfall meant to be a low thud arrived as a tone. The forest floor
  // maps to dirt, so it fired on every single step. Filtered noise, like the
  // leaves and grass layers, and like stone now is.
  dirt: {
    method: 'noise', freq: 260, oscType: 'sine',
    dur: 0.085, filterType: 'lowpass', filterQ: 0.7, pitchDecay: 1,
  },
  // Stone was a pure 180Hz sine sweeping to 36Hz — a synthesized beep, not a
  // footfall, and the most audible layer in a flagstone clearing. A real step
  // on stone is a broadband transient, so it is filtered noise now.
  stone: {
    method: 'noise', freq: 1800, oscType: 'sine',
    dur: 0.045, filterType: 'bandpass', filterQ: 1.1, pitchDecay: 1,
  },
};

const TERRAIN_MIX_PROFILES: Record<PaintedTerrainType, FootstepMixProfile> = {
  grass:      { leaves: 0.03, grass: 0.08, dirt: 0.02, stone: 0.00 },
  leaves:     { leaves: 0.09, grass: 0.03, dirt: 0.02, stone: 0.00 },
  dirt:       { leaves: 0.00, grass: 0.02, dirt: 0.09, stone: 0.00 },
  stone:      { leaves: 0.00, grass: 0.00, dirt: 0.03, stone: 0.08 },
  water_edge: { leaves: 0.00, grass: 0.00, dirt: 0.00, stone: 0.00 },
};

// ── Ambient profile resolution ────────────────────────────────────────────

function resolveAmbientProfile(ambientKey: string): AmbientProfile {
  const suffix = ambientKey.replace(/^ambient-[a-z]+-/, '');
  const map: Record<string, AmbientProfile> = {
    wind: 'wind',
    fire: 'fire',
    meadow: 'meadow',
    birch: 'birch',
    brook: 'brook',
    village: 'village',
    oven: 'oven',
    chimes: 'chimes',
    forge: 'forge',
    glow: 'glow',
    echo: 'echo',
    steam: 'steam',
    silence: 'silence',
    ice: 'ice',
    drip: 'drip',
    hearth: 'hearth',
    crunch: 'crunch',
    warmth: 'hearth',
  };
  return map[suffix] ?? 'wind';
}

// ── Manager ───────────────────────────────────────────────────────────────

export class PaintedSceneAudioManager {
  private ctx: AudioContext;
  private masterGain: GainNode;
  private ambientGain: GainNode;
  private sfxGain: GainNode;

  private ambientSlots: [AmbientSlot | null, AmbientSlot | null] = [null, null];
  private ambientSlotIdx = 0;
  private currentAmbientKey: string | null = null;
  private ambientBuffers: Map<string, AudioBuffer> = new Map();

  private currentTerrain: PaintedTerrainType = 'grass';
  private lastFootstepMs = 0;
  private footIndex = 0;
  private walkerScaleGain = 1;

  private unlocked = false;
  private destroyed = false;

  constructor() {
    this.ctx = new AudioContext();

    this.masterGain = this.ctx.createGain();
    this.masterGain.connect(this.ctx.destination);

    this.ambientGain = this.ctx.createGain();
    this.ambientGain.connect(this.masterGain);

    this.sfxGain = this.ctx.createGain();
    this.sfxGain.connect(this.masterGain);

    this.applySettings();
    this.setupAutoUnlock();
  }

  // ── Settings ────────────────────────────────────────────────────────────

  applySettings(muted = false): void {
    const s = loadSettings();
    const effective = muted ? 0 : s.masterVolume;
    const now = this.ctx.currentTime;
    this.masterGain.gain.setValueAtTime(effective, now);
    this.ambientGain.gain.setValueAtTime(s.musicVolume * 0.6, now);
    this.sfxGain.gain.setValueAtTime(s.sfxVolume, now);
  }

  // ── Ambient beds ────────────────────────────────────────────────────────

  setAmbient(ambientKey: string): void {
    if (ambientKey === this.currentAmbientKey) return;
    this.ensureUnlocked();

    const buffer = this.getOrCreateAmbientBuffer(ambientKey);
    const fadeSec = AMBIENT_CROSSFADE_MS / 1000;
    const now = this.ctx.currentTime;

    const active = this.ambientSlots[this.ambientSlotIdx];
    if (active) {
      active.gain.gain.setValueAtTime(active.gain.gain.value, now);
      active.gain.gain.linearRampToValueAtTime(0, now + fadeSec);
      const src = active.source;
      setTimeout(() => { try { src.stop(); } catch { /* already stopped */ } }, AMBIENT_CROSSFADE_MS + 150);
    }

    this.ambientSlotIdx = this.ambientSlotIdx === 0 ? 1 : 0;
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(1, now + fadeSec);
    source.connect(gain).connect(this.ambientGain);
    source.start();

    this.ambientSlots[this.ambientSlotIdx] = { source, gain };
    this.currentAmbientKey = ambientKey;
  }

  stopAmbient(): void {
    const fadeSec = AMBIENT_CROSSFADE_MS / 1000;
    const now = this.ctx.currentTime;
    for (const slot of this.ambientSlots) {
      if (slot) {
        slot.gain.gain.setValueAtTime(slot.gain.gain.value, now);
        slot.gain.gain.linearRampToValueAtTime(0, now + fadeSec);
        const src = slot.source;
        setTimeout(() => { try { src.stop(); } catch { /* already stopped */ } }, AMBIENT_CROSSFADE_MS + 150);
      }
    }
    this.ambientSlots = [null, null];
    this.ambientSlotIdx = 0;
    this.currentAmbientKey = null;
  }

  /** Phase-aware ambient key (e.g. "ambient-hill-meadow" at dawn → "ambient-hill-silence" at night). */
  setPhaseAmbient(phaseAmbientKey: string | undefined, baseAmbientKey: string): void {
    this.setAmbient(phaseAmbientKey ?? baseAmbientKey);
  }

  // ── Terrain & footsteps ─────────────────────────────────────────────────

  setTerrain(terrain: PaintedTerrainType): void {
    this.currentTerrain = terrain;
  }

  setWalkerScale(scale: number): void {
    this.walkerScaleGain = Math.max(0.15, Math.min(1, scale));
  }

  /**
   * Time-gated footstep, for callers with no gait to sync to.
   *
   * The interval is a wall clock and knows nothing about the legs, so a step
   * lands whenever 350ms have passed rather than when a foot does. Prefer
   * `playFootstepNow()`, driven by the rig's contact events.
   */
  playFootstep(nowMs: number): void {
    if (nowMs - this.lastFootstepMs < FOOTSTEP_INTERVAL_MS) return;
    this.lastFootstepMs = nowMs;
    this.ensureUnlocked();
    this.playTerrainFootstep();
  }

  /** Footstep on an actual foot contact. No interval gate — the gait is the gate. */
  playFootstepNow(): void {
    this.lastFootstepMs = performance.now();
    this.ensureUnlocked();
    this.playTerrainFootstep();
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.stopAmbient();
    this.ctx.close();
  }

  // ── Internal: ambient buffer generation ─────────────────────────────────

  private getOrCreateAmbientBuffer(ambientKey: string): AudioBuffer {
    const cached = this.ambientBuffers.get(ambientKey);
    if (cached) return cached;
    const profile = resolveAmbientProfile(ambientKey);
    const buf = this.generateAmbientBuffer(profile);
    this.ambientBuffers.set(ambientKey, buf);
    return buf;
  }

  private generateAmbientBuffer(profile: AmbientProfile): AudioBuffer {
    const sampleRate = this.ctx.sampleRate;
    const len = sampleRate * AMBIENT_BUFFER_DURATION;
    const buf = this.ctx.createBuffer(2, len, sampleRate);
    const left = buf.getChannelData(0);
    const right = buf.getChannelData(1);

    switch (profile) {
      case 'wind': {
        this.fillFilteredNoise(left, sampleRate, 'lowpass', 400, 0.06);
        this.fillFilteredNoise(right, sampleRate, 'lowpass', 420, 0.055);
        break;
      }
      case 'meadow': {
        this.fillFilteredNoise(left, sampleRate, 'lowpass', 350, 0.045);
        this.fillFilteredNoise(right, sampleRate, 'lowpass', 370, 0.04);
        this.addBirdChirps(left, sampleRate, len, 2200, 2.0, 0.012);
        this.addBirdChirps(right, sampleRate, len, 2600, 2.4, 0.010);
        break;
      }
      case 'birch': {
        this.fillFilteredNoise(left, sampleRate, 'bandpass', 600, 0.05);
        this.fillFilteredNoise(right, sampleRate, 'bandpass', 650, 0.045);
        this.addBirdChirps(left, sampleRate, len, 2400, 1.8, 0.014);
        break;
      }
      case 'brook': {
        this.fillFilteredNoise(left, sampleRate, 'bandpass', 1200, 0.04);
        this.fillFilteredNoise(right, sampleRate, 'bandpass', 1400, 0.035);
        this.fillFilteredNoise(left, sampleRate, 'lowpass', 300, 0.025);
        this.fillFilteredNoise(right, sampleRate, 'lowpass', 320, 0.02);
        break;
      }
      case 'fire':
      case 'hearth': {
        this.fillFilteredNoise(left, sampleRate, 'bandpass', 400, 0.055);
        this.fillFilteredNoise(right, sampleRate, 'bandpass', 450, 0.05);
        this.addCrackles(left, sampleRate, len, 0.04);
        this.addCrackles(right, sampleRate, len, 0.035);
        break;
      }
      case 'forge': {
        this.fillFilteredNoise(left, sampleRate, 'bandpass', 300, 0.06);
        this.fillFilteredNoise(right, sampleRate, 'bandpass', 350, 0.055);
        this.addCrackles(left, sampleRate, len, 0.05);
        this.addCrackles(right, sampleRate, len, 0.045);
        // Low rumble
        for (let i = 0; i < len; i++) {
          const t = i / sampleRate;
          left[i] += Math.sin(2 * Math.PI * 45 * t) * 0.012;
          right[i] += Math.sin(2 * Math.PI * 42 * t) * 0.01;
        }
        break;
      }
      case 'village':
      case 'oven': {
        this.fillFilteredNoise(left, sampleRate, 'lowpass', 300, 0.035);
        this.fillFilteredNoise(right, sampleRate, 'lowpass', 320, 0.03);
        for (let i = 0; i < len; i++) {
          const t = i / sampleRate;
          left[i] += Math.sin(2 * Math.PI * 60 * t) * 0.005;
        }
        break;
      }
      case 'chimes': {
        this.fillFilteredNoise(left, sampleRate, 'lowpass', 350, 0.03);
        this.fillFilteredNoise(right, sampleRate, 'lowpass', 370, 0.025);
        this.addChimeTones(left, sampleRate, len);
        this.addChimeTones(right, sampleRate, len);
        break;
      }
      case 'glow': {
        this.fillFilteredNoise(left, sampleRate, 'lowpass', 250, 0.04);
        this.fillFilteredNoise(right, sampleRate, 'lowpass', 270, 0.035);
        for (let i = 0; i < len; i++) {
          const t = i / sampleRate;
          left[i] += Math.sin(2 * Math.PI * 80 * t) * 0.008;
          right[i] += Math.sin(2 * Math.PI * 83 * t) * 0.007;
        }
        break;
      }
      case 'echo': {
        this.fillFilteredNoise(left, sampleRate, 'bandpass', 800, 0.035);
        this.fillFilteredNoise(right, sampleRate, 'bandpass', 900, 0.03);
        break;
      }
      case 'steam': {
        this.fillFilteredNoise(left, sampleRate, 'bandpass', 1600, 0.04);
        this.fillFilteredNoise(right, sampleRate, 'bandpass', 1800, 0.035);
        break;
      }
      case 'ice': {
        this.fillFilteredNoise(left, sampleRate, 'highpass', 2000, 0.025);
        this.fillFilteredNoise(right, sampleRate, 'highpass', 2200, 0.02);
        this.fillFilteredNoise(left, sampleRate, 'lowpass', 200, 0.03);
        this.fillFilteredNoise(right, sampleRate, 'lowpass', 220, 0.025);
        break;
      }
      case 'drip': {
        this.fillFilteredNoise(left, sampleRate, 'lowpass', 300, 0.03);
        this.fillFilteredNoise(right, sampleRate, 'lowpass', 320, 0.025);
        this.addDrips(left, sampleRate, len, 0.03);
        this.addDrips(right, sampleRate, len, 0.025);
        break;
      }
      case 'crunch': {
        this.fillFilteredNoise(left, sampleRate, 'bandpass', 500, 0.04);
        this.fillFilteredNoise(right, sampleRate, 'bandpass', 550, 0.035);
        break;
      }
      case 'silence': {
        this.fillFilteredNoise(left, sampleRate, 'lowpass', 150, 0.015);
        this.fillFilteredNoise(right, sampleRate, 'lowpass', 160, 0.012);
        break;
      }
    }

    // Loop-point fade (10 ms)
    const fadeLen = Math.floor(sampleRate * 0.01);
    for (let i = 0; i < fadeLen && i < len; i++) {
      const env = i / fadeLen;
      left[i] *= env;
      right[i] *= env;
      left[len - 1 - i] *= env;
      right[len - 1 - i] *= env;
    }

    return buf;
  }

  private fillFilteredNoise(
    data: Float32Array,
    sampleRate: number,
    type: 'lowpass' | 'highpass' | 'bandpass',
    freq: number,
    gain: number,
  ): void {
    const raw = new Float32Array(data.length);
    for (let i = 0; i < raw.length; i++) raw[i] = Math.random() * 2 - 1;

    const nyq = sampleRate / 2;
    const alpha = Math.min(0.99, freq / nyq);

    if (type === 'lowpass') {
      let prev = 0;
      for (let i = 0; i < raw.length; i++) {
        prev = alpha * raw[i] + (1 - alpha) * prev;
        data[i] += prev * gain;
      }
    } else if (type === 'highpass') {
      let prev = 0, prevRaw = 0;
      for (let i = 0; i < raw.length; i++) {
        const hp = alpha * (prev + raw[i] - prevRaw);
        prevRaw = raw[i];
        prev = hp;
        data[i] += hp * gain;
      }
    } else {
      const alphaHp = Math.min(0.99, (freq * 0.4) / nyq);
      const alphaLp = Math.min(0.99, (freq * 2.5) / nyq);
      let hp = 0, prevRawHp = 0, lp = 0;
      for (let i = 0; i < raw.length; i++) {
        hp = alphaHp * (hp + raw[i] - prevRawHp);
        prevRawHp = raw[i];
        lp = alphaLp * hp + (1 - alphaLp) * lp;
        data[i] += lp * gain;
      }
    }
  }

  /**
   * A bird, rather than a beep.
   *
   * This used to write a fixed-frequency sine straight into the buffer on a
   * fixed interval: `sin(2*PI*freq*t)` at 2400Hz every 1.8s. That is a smoke
   * alarm, not a chirp, and because it bypasses createOscillator it does not
   * show up when you go looking for stray tones. It was audible in every scene
   * using the meadow or birch profile.
   *
   * Real birdsong glissandos — the pitch sweeps across the note — and carries
   * a harmonic. It also does not arrive on a metronome, which matters doubly
   * here because the ambient buffer LOOPS: anything strictly periodic beats
   * against the loop and turns into a rhythm.
   *
   * Deterministic per call, so a given profile always renders the same bed.
   */
  private addBirdChirps(
    data: Float32Array, sampleRate: number, len: number,
    freq: number, period: number, amp: number,
  ): void {
    // A cheap seeded RNG: the bed must be identical every time it is generated,
    // or the same scene sounds different on each visit.
    let seed = Math.round(freq * 7 + period * 1000);
    const rnd = () => {
      seed = (seed * 1664525 + 1013904223) % 4294967296;
      return seed / 4294967296;
    };

    const basePeriod = Math.floor(sampleRate * period);
    for (let slot = 0; slot * basePeriod < len; slot++) {
      // Jitter well inside the slot so chirps never land on a grid.
      const jitter = Math.floor((rnd() - 0.5) * basePeriod * 0.7);
      const start = slot * basePeriod + Math.floor(basePeriod * 0.5) + jitter;
      if (start < 0 || start >= len) continue;

      const dur = 0.045 + rnd() * 0.05;
      const chirpLen = Math.floor(sampleRate * dur);
      // Sweep up or down across a third or so, the way a small bird does.
      const f0 = freq * (0.86 + rnd() * 0.2);
      const f1 = f0 * (rnd() < 0.5 ? 1.28 + rnd() * 0.2 : 0.74 - rnd() * 0.1);
      const gain = amp * (0.55 + rnd() * 0.6);

      let phase = 0;
      for (let i = 0; i < chirpLen && start + i < len; i++) {
        const u = i / chirpLen;
        const f = f0 + (f1 - f0) * u;
        phase += (2 * Math.PI * f) / sampleRate;
        // Soft attack, longer decay — a struck note, not a gated tone.
        const env = Math.sin(Math.PI * Math.pow(u, 0.7)) * (1 - u * 0.35);
        const harmonic = Math.sin(phase * 2) * 0.22;
        data[start + i] += (Math.sin(phase) + harmonic) * gain * env;
      }
    }
  }

  private addCrackles(data: Float32Array, sampleRate: number, len: number, amp: number): void {
    const crackleInterval = Math.floor(sampleRate * 0.8);
    const crackleLen = Math.floor(sampleRate * 0.03);
    for (let start = Math.floor(sampleRate * 0.2); start < len; start += crackleInterval) {
      for (let i = 0; i < crackleLen && start + i < len; i++) {
        const env = Math.pow(1 - i / crackleLen, 2);
        data[start + i] += (Math.random() * 2 - 1) * amp * env;
      }
    }
  }

  private addChimeTones(data: Float32Array, sampleRate: number, len: number): void {
    const notes = [523.3, 659.3, 784.0];
    const chimePeriod = Math.floor(sampleRate * 1.3);
    let noteIdx = 0;
    for (let start = 0; start < len; start += chimePeriod) {
      const freq = notes[noteIdx % notes.length];
      noteIdx++;
      const chimeLen = Math.floor(sampleRate * 0.4);
      for (let i = 0; i < chimeLen && start + i < len; i++) {
        const t = i / sampleRate;
        const env = Math.pow(1 - i / chimeLen, 1.5);
        data[start + i] += Math.sin(2 * Math.PI * freq * t) * 0.012 * env;
      }
    }
  }

  private addDrips(data: Float32Array, sampleRate: number, len: number, amp: number): void {
    const dripInterval = Math.floor(sampleRate * 1.5);
    const dripLen = Math.floor(sampleRate * 0.05);
    for (let start = Math.floor(sampleRate * 0.5); start < len; start += dripInterval) {
      for (let i = 0; i < dripLen && start + i < len; i++) {
        const t = i / sampleRate;
        const env = Math.pow(1 - i / dripLen, 3);
        data[start + i] += Math.sin(2 * Math.PI * 1200 * t) * amp * env;
      }
    }
  }

  // ── Internal: footstep playback ─────────────────────────────────────────

  private playTerrainFootstep(): void {
    const now = this.ctx.currentTime;

    if (this.currentTerrain === 'water_edge') {
      this.playWaterSplash(now);
      return;
    }

    const profile = TERRAIN_MIX_PROFILES[this.currentTerrain];
    const foot = this.footIndex;
    this.footIndex = 1 - this.footIndex;

    const stepGain = this.ctx.createGain();
    stepGain.gain.value = this.walkerScaleGain;
    stepGain.connect(this.sfxGain);

    const layers: FootstepLayer[] = ['leaves', 'grass', 'dirt', 'stone'];
    for (const layer of layers) {
      const baseGain = profile[layer];
      if (baseGain <= 0) continue;

      const lp = FOOTSTEP_LAYERS[layer];
      const gainVar = 1 + (Math.random() * 0.4 - 0.2);
      const pitchVar = 1 + (Math.random() * 0.2 - 0.1);
      const footDelay = foot * (Math.random() * 0.008);
      const layerGain = baseGain * gainVar;
      const layerStart = now + footDelay;

      if (lp.method === 'noise') {
        this.playFootstepNoiseLayer(layerStart, lp, layerGain, pitchVar, stepGain);
      } else {
        this.playFootstepOscLayer(layerStart, lp, layerGain, pitchVar, stepGain);
      }
    }
  }

  private playFootstepOscLayer(
    start: number, lp: FootstepLayerParams,
    gain: number, pitchVar: number, dest: AudioNode,
  ): void {
    const freq = lp.freq * pitchVar;
    const osc = this.ctx.createOscillator();
    osc.type = lp.oscType;
    osc.frequency.setValueAtTime(freq, start);
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(20, freq * lp.pitchDecay), start + lp.dur,
    );
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(gain, start);
    g.gain.exponentialRampToValueAtTime(0.001, start + lp.dur);
    osc.connect(g).connect(dest);
    osc.start(start);
    osc.stop(start + lp.dur + 0.05);
  }

  private playFootstepNoiseLayer(
    start: number, lp: FootstepLayerParams,
    gain: number, pitchVar: number, dest: AudioNode,
  ): void {
    const len = Math.ceil(this.ctx.sampleRate * lp.dur);
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 1.8);
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buf;

    const filter = this.ctx.createBiquadFilter();
    filter.type = lp.filterType ?? 'lowpass';
    filter.frequency.value = lp.freq * pitchVar;
    if (lp.filterQ !== undefined) filter.Q.value = lp.filterQ;

    const g = this.ctx.createGain();
    g.gain.setValueAtTime(gain, start);
    g.gain.exponentialRampToValueAtTime(0.001, start + lp.dur);

    src.connect(filter).connect(g).connect(dest);
    src.start(start);
    src.stop(start + lp.dur + 0.05);
  }

  private playWaterSplash(now: number): void {
    const dur = 0.14;
    const gain = 0.07;
    const len = Math.ceil(this.ctx.sampleRate * dur);
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2);
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const lpf = this.ctx.createBiquadFilter();
    lpf.type = 'lowpass';
    lpf.frequency.value = 800;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(gain, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + dur);
    src.connect(lpf).connect(g).connect(this.sfxGain);
    src.start();
    src.stop(now + dur + 0.05);
  }

  // ── Autoplay unlock ─────────────────────────────────────────────────────

  private setupAutoUnlock(): void {
    if (this.ctx.state === 'running') {
      this.unlocked = true;
      return;
    }
    const unlock = (): void => {
      if (this.unlocked) return;
      this.ctx.resume().then(() => {
        this.unlocked = true;
        document.removeEventListener('click', unlock);
        document.removeEventListener('keydown', unlock);
        document.removeEventListener('touchstart', unlock);
      });
    };
    document.addEventListener('click', unlock);
    document.addEventListener('keydown', unlock);
    document.addEventListener('touchstart', unlock);
  }

  private ensureUnlocked(): void {
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }
}
